export type PresenceActivityEvent = {
  timestamp?: number | string
  time?: string
  message?: unknown
  displayTemplates?: unknown
  details?: unknown
  event?: unknown
}

export type PresenceActivityPoint = {
  ts: number
  occupied: boolean
}

function parseBooleanLike(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return null
    return value > 0
  }
  if (typeof value !== 'string') return null
  const token = value.trim().toLowerCase()
  if (!token) return null
  if (['true', '1', 'on', 'yes', 'active', 'occupied', 'present', 'opened', 'open', 'motiondetected'].includes(token)) return true
  if (['false', '0', 'off', 'no', 'inactive', 'unoccupied', 'absent', 'closed', 'close', 'idle', 'clear'].includes(token)) return false
  return null
}

function parseDetectedToken(value: unknown): boolean | null {
  const token = String(value ?? '').trim().toLowerCase()
  if (!token) return null
  if (
    token.includes('open')
    || token.includes('motion')
    || token.includes('occup')
    || token.includes('present')
    || token.includes('detect')
    || token === 'active'
  ) return true
  if (
    token.includes('close')
    || token.includes('idle')
    || token.includes('clear')
    || token.includes('absent')
    || token.includes('unoccup')
    || token.includes('inactive')
  ) return false
  return null
}

function normalizeTimestamp(raw: unknown): number | null {
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return raw > 1e12 ? raw : raw * 1000
  }
  if (typeof raw === 'string' && raw.trim()) {
    const parsed = Date.parse(raw)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

function resolveEventTimestamp(event: PresenceActivityEvent): number | null {
  return (
    normalizeTimestamp(event.timestamp)
    ?? normalizeTimestamp(event.time)
    ?? null
  )
}

function resolveOccupancyFromTemplate(template: unknown): boolean | null {
  const token = String(template ?? '').trim().toLowerCase()
  if (!token) return null
  if (
    token.includes('occupancy_true')
    || token.includes('presence_true')
    || token.includes('detected_true')
    || token.includes('motion_true')
  ) return true
  if (
    token.includes('occupancy_false')
    || token.includes('presence_false')
    || token.includes('detected_false')
    || token.includes('motion_false')
  ) return false
  return null
}

function resolveOccupancyFromMessage(message: unknown): boolean | null {
  if (!message || typeof message !== 'object') return null
  const obj = message as Record<string, unknown>

  const occupancy = parseBooleanLike(obj.occupancy)
  if (occupancy != null) return occupancy

  const presence = parseBooleanLike(obj.presence)
  if (presence != null) return presence

  if (Array.isArray(obj.detected) && obj.detected.length > 0) {
    const detected = parseDetectedToken(obj.detected[0])
    if (detected != null) return detected
  }
  const detectedBool = parseBooleanLike(obj.detected)
  if (detectedBool != null) return detectedBool

  return null
}

function resolveOccupancyFromDetails(details: unknown): boolean | null {
  if (!details || typeof details !== 'object') return null
  const obj = details as Record<string, unknown>
  const actionType = String(obj.actionType ?? '').toLowerCase()
  if (!actionType) return null
  if (!['occupancy', 'presence', 'detected', 'motion'].includes(actionType)) return null
  const value = obj.actionValue ?? obj.value
  return parseBooleanLike(value) ?? parseDetectedToken(value)
}

function resolveEventOccupancy(event: PresenceActivityEvent): boolean | null {
  const fromMessage = resolveOccupancyFromMessage(event.message)
  if (fromMessage != null) return fromMessage

  if (Array.isArray(event.displayTemplates)) {
    for (const item of event.displayTemplates) {
      const fromTemplate = resolveOccupancyFromTemplate(item)
      if (fromTemplate != null) return fromTemplate
    }
  }

  const fromDetails = resolveOccupancyFromDetails(event.details)
  if (fromDetails != null) return fromDetails

  return parseDetectedToken(event.event)
}

export function extractPresenceActivityPoints(events: unknown): PresenceActivityPoint[] {
  if (!Array.isArray(events)) return []
  const points: PresenceActivityPoint[] = []
  for (const raw of events) {
    if (!raw || typeof raw !== 'object') continue
    const event = raw as PresenceActivityEvent
    const ts = resolveEventTimestamp(event)
    const occupied = resolveEventOccupancy(event)
    if (ts == null || occupied == null) continue
    points.push({ ts, occupied })
  }
  return points.sort((a, b) => a.ts - b.ts)
}

export function buildPresenceActivityBars(
  points: PresenceActivityPoint[],
  nowMs = Date.now(),
  slotCount = 10
): number[] {
  if (slotCount <= 0) return []
  const windowMs = 24 * 60 * 60 * 1000
  const startMs = nowMs - windowMs
  const slotMs = windowMs / slotCount
  const counts = new Array<number>(slotCount).fill(0)

  for (const point of points) {
    if (!point.occupied) continue
    if (point.ts < startMs || point.ts > nowMs) continue
    const idx = Math.min(
      slotCount - 1,
      Math.max(0, Math.floor((point.ts - startMs) / slotMs))
    )
    counts[idx] += 1
  }

  const maxCount = counts.reduce((acc, cur) => (cur > acc ? cur : acc), 0)
  if (maxCount <= 0) return new Array<number>(slotCount).fill(0)

  return counts.map((count) => {
    if (count <= 0) return 12
    return Math.min(100, Math.max(18, Math.round((count / maxCount) * 100)))
  })
}
